/**
 * Feed sequencer for listing pages.
 *
 * Slot pattern (per page of 25, page 1):
 *   1  Super Hot     2  Super Hot     3  Project Banner
 *   4  Super Hot     5  Super Hot     6  Project Banner
 *   7  Super Hot     8  Super Hot     9  Super Hot     10 Super Hot
 *   11 Hot           12 Hot           13 Hot           14 Hot   15 Hot
 *   16–25 Hot or Normal
 *
 * Designated pools are filled first; if the pool is empty we fall back
 * down the tier ladder (super_hot -> hot -> standard -> normal) so the
 * feed never has blank slots even if inventory is thin.
 */

export type Tier = 'super_hot' | 'hot' | 'standard' | null | undefined;

export type FeedSlot =
  | { kind: 'property'; position: number; property: any }
  | { kind: 'project-banner'; position: number; project: any };

// Positions inside a page of 25 (1-indexed)
const SUPER_HOT_POS = new Set([1, 2, 4, 5, 7, 8, 9, 10]);
const HOT_POS = new Set([11, 12, 13, 14, 15]);
const HOT_OR_NORMAL_POS = new Set([16, 17, 18, 19, 20, 21, 22, 23, 24, 25]);
const BANNER_POS = [3, 6];

function tierOf(p: any): Tier {
  return (p?.featured_tier ?? null) as Tier;
}

interface BuildFeedOpts {
  properties: any[];
  projectBanners?: any[];
  pageSize?: number;
  /** When true (page > 1), skip banner slots and just place properties. */
  skipBanners?: boolean;
}

export function buildFeed({
  properties,
  projectBanners = [],
  pageSize = 25,
  skipBanners = false,
}: BuildFeedOpts): FeedSlot[] {
  // Partition into mutable pools by tier
  const superHot = properties.filter(p => tierOf(p) === 'super_hot');
  const hot = properties.filter(p => tierOf(p) === 'hot');
  const standard = properties.filter(p => tierOf(p) === 'standard');
  const normal = properties.filter(p => !tierOf(p));

  // Take from pools in order, falling back down the ladder
  const takeFrom = (...pools: any[][]) => {
    for (const pool of pools) {
      if (pool.length > 0) return pool.shift();
    }
    return null;
  };

  const slots: FeedSlot[] = [];
  const banners = [...projectBanners];

  for (let pos = 1; pos <= pageSize; pos++) {
    // Banner slot
    if (!skipBanners && BANNER_POS.includes(pos) && banners.length > 0) {
      slots.push({ kind: 'project-banner', position: pos, project: banners.shift() });
      continue;
    }

    let prop: any = null;
    if (SUPER_HOT_POS.has(pos)) {
      prop = takeFrom(superHot, hot, standard, normal);
    } else if (HOT_POS.has(pos)) {
      prop = takeFrom(hot, superHot, standard, normal);
    } else if (HOT_OR_NORMAL_POS.has(pos)) {
      // Prefer hot/standard then normal, then any leftover super_hot
      prop = takeFrom(hot, standard, normal, superHot);
    } else {
      prop = takeFrom(normal, standard, hot, superHot);
    }

    if (!prop) break; // out of inventory
    slots.push({ kind: 'property', position: pos, property: prop });
  }

  return slots;
}
